import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, map, Observable} from 'rxjs';
import {GridGetService, GridServiceParams, GridServiceResponse} from 'src/app/shared/model/page-grid';
import {removeEmptyParams} from 'src/infrastructure/helper/remove-empty-params';
import {ExportPdfService} from 'src/app/shared/service/files/export-pdf.service';
import {ClientBalanceResponse} from '../models/client-balance';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class ClientBalanceGridService implements GridGetService<ClientBalanceResponse> {
  private readonly baseUrl = 'dbov2/client-balances';
  private readonly http = inject(HttpClient);
  private readonly pdfService = inject(ExportPdfService);
  private getAutocompleteValue(value: any): string {
    if (value && typeof value === 'object' && 'value' in value) {
      return value.value;
    }
    return value || '';
  }

  private extractFilterValue(val: any): string | number | null {
    if (val && typeof val === 'object') {
      if ('id' in val) return val.id;
      if ('value' in val) return val.value;
    }
    if (typeof val === 'string' && val.trim() !== '') return val;
    if (typeof val === 'number') return val;
    return null;
  }


  getItems({filters, page, sort} : GridServiceParams): Observable<GridServiceResponse<ClientBalanceResponse>>  {
    const params:Record<string, string | number | boolean> = {
        pageSize:  page?.pageSize || 25,
        pageNumber : (page?.pageIndex || 0) + 1,
        propertyName: sort?.active || '',
        orderKey: sort?.direction || '',

        asOfDate: filters?.['asOfDate'] ? moment(filters?.['asOfDate']).format('YYYY-MM-DD') : '',
        paymentDateFrom: filters?.['paymentDateFrom'] ? moment(filters?.['paymentDateFrom']).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '',
        paymentDateTo: filters?.['paymentDateTo'] ? moment(filters?.['paymentDateTo']).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '',
        clientName: this.getAutocompleteValue(filters?.['clientName']),
        minOutstandingBalance: filters?.['minOutstandingBalance'] ?? '',
        maxOutstandingBalance: filters?.['maxOutstandingBalance'] ?? '',
        minTotalBilled: filters?.['minTotalBilled'] ?? '',
        maxTotalBilled: filters?.['maxTotalBilled'] ?? '',
        dataSourceId: filters?.['dataSourceId'] ?? '',
        clientPayStatusId: filters?.['clientPayStatusId'] ?? '',
    };

    const clientId = this.extractFilterValue(filters?.['id']);
    if (clientId !== null) {
      params['id'] = clientId;
    }

    removeEmptyParams(params);

    return this.http.get<GridServiceResponse<ClientBalanceResponse>>(`${this.baseUrl}`, { params: {...params} })
    .pipe(catchError((error:HttpErrorResponse)=>{
        throw new Error(error?.message || 'Server error');
    }));
  }
  getPdf({filters, page}: GridServiceParams): Observable<void> {
    const params:Record<string, string | number | boolean> = {
      exportToPdf: true,
      format: 'pdf',
      formatCurrency: 'true',
      hideCurrencySymbol: 'true',
      currencyCode: 'USD',
      currencySymbol: '$',
      currencyFormat: '0.00',
      PropertyName: 'clientName',
      OrderKey: 'desc',
      pageSize:  page?.totalItemCount || 100,

      asOfDate: filters?.['asOfDate'] ? moment(filters?.['asOfDate']).format('YYYY-MM-DD') : '',
      paymentDate: filters?.['paymentDate'] ? moment(filters?.['paymentDate']).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : '',
      clientName: this.getAutocompleteValue(filters?.['clientName']),
      id: this.getAutocompleteValue(filters?.['id']),
      minOutstandingBalance: filters?.['minOutstandingBalance'] ?? '',
      maxOutstandingBalance: filters?.['maxOutstandingBalance'] ?? '',
      minTotalBilled: filters?.['minTotalBilled'] ?? '',
      maxTotalBilled: filters?.['maxTotalBilled'] ?? '',
      dataSourceId: filters?.['dataSourceId'] ?? '',
      clientPayStatusId: filters?.['clientPayStatusId'] ?? '',
    };

    removeEmptyParams(params);

    return this.http.get<{ pdfBase64: string }>(`${this.baseUrl}`, {params: { ...params }})
    .pipe(
      map(response => {
        if (response && response.pdfBase64) {
            const timestamp = new Date().toISOString().replace(/[-:T]/g, '').split('.')[0];
            const fileName = `client-balance_${timestamp}.pdf`;
            this.pdfService.savePdfFromBase64(response.pdfBase64, fileName);
        } else {
          console.error('Invalid PDF response:', response);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('PDF download error:', error);
        throw new Error(error?.message || 'PDF generation failed');
      })
    );
  }
}
